/**
 * PastResultsPage - View saved forecast and optimization results.
 *
 * Route: /communities/:id/results
 *
 * Features:
 *   - Tab navigation: Forecasts | Optimizations
 *   - Table of saved results with key KPIs
 *   - View (modal), Export, Delete actions per result
 *   - Modal displays full result data in tabular form with export button
 */

import React, { useState, useEffect, useCallback } from 'react'
import { useOutletContext, useParams } from 'react-router-dom'
import { Card, Tab, Nav, Table, Button, Badge, Alert, Spinner, Modal } from 'react-bootstrap'
import { resultsAPI } from '../api/results'
import { exportOptimizationResults, exportForecastShortTerm, exportForecastLongTerm, exportCBAResults } from '../utils/exportCSV'
import { useAuth } from '../context/AuthContext'
import { useTranslation } from 'react-i18next'

// ---------------------------------------------------------------------------
// Export helper — dispatches to the correct exporter based on result type
// ---------------------------------------------------------------------------
function exportResult(result, currency) {
  if (!result?.result_data) return
  const rd = result.result_data
  const params = result.parameters || {}

  if (result.result_type === 'forecast') {
    const mode = params.forecast_mode || 'short_term'
    const assetName = params.asset || 'forecast'
    if (mode === 'long_term') {
      exportForecastLongTerm(rd, assetName)
    } else {
      exportForecastShortTerm(rd, assetName, params.target_date)
    }
  } else if (result.result_type === 'cba') {
    exportCBAResults(rd, currency)
  } else {
    exportOptimizationResults(rd, result.created_at, currency)
  }
}

// ---------------------------------------------------------------------------
// ResultViewModal — renders result data as a table inside a modal
// ---------------------------------------------------------------------------
function ResultViewModal({ show, onHide, result, currency }) {
  const { t } = useTranslation()
  if (!result) return null

  const rd = result.result_data || {}
  const params = result.parameters || {}
  const resultType = result.result_type

  // --- Forecast: hourly or monthly table ---
  const renderForecastTable = () => {
    const isLongTerm = params.forecast_mode === 'long_term'

    if (isLongTerm) {
      const months = rd.months || []
      const forecast = rd.forecast || []
      const lower = rd.lower || []
      const upper = rd.upper || []
      if (months.length === 0 && forecast.length === 0) return <p className="text-muted">{t('past_results.modal_no_data')}</p>
      const rows = months.length > 0 ? months : forecast.map((_, i) => `Month ${i + 1}`)
      return (
        <Table striped bordered size="sm" responsive style={{ fontSize: 'var(--font-size-sm)' }}>
          <thead style={{ backgroundColor: 'var(--color-neutral-50)' }}>
            <tr>
              <th>{t('past_results.th_month')}</th>
              <th className="text-end">{t('past_results.th_forecast_kwh')}</th>
              <th className="text-end">{t('past_results.th_lower_bound')}</th>
              <th className="text-end">{t('past_results.th_upper_bound')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((m, i) => (
              <tr key={i}>
                <td style={{ fontWeight: 500 }}>{m}</td>
                <td className="text-end">{forecast[i] != null ? forecast[i].toFixed(1) : '—'}</td>
                <td className="text-end">{lower[i] != null ? lower[i].toFixed(1) : '—'}</td>
                <td className="text-end">{upper[i] != null ? upper[i].toFixed(1) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      )
    }

    // Short-term hourly
    const hours = rd.hours || []
    const forecast = rd.forecast || []
    const lower = rd.lower || []
    const upper = rd.upper || []
    if (hours.length === 0 && forecast.length === 0) return <p className="text-muted">{t('past_results.modal_no_data')}</p>
    const rowCount = Math.max(hours.length, forecast.length)

    return (
      <>
        {/* Summary row */}
        <div className="d-flex gap-4 mb-3 flex-wrap" style={{ fontSize: 'var(--font-size-sm)' }}>
          {rd.peak != null && (
            <div><strong>{t('past_results.summary_peak')}</strong> {rd.peak.toFixed(2)} kW</div>
          )}
          {rd.total != null && (
            <div><strong>{t('past_results.summary_total')}</strong> {rd.total.toFixed(2)} kWh</div>
          )}
          {rd.avg != null && (
            <div><strong>{t('past_results.summary_average')}</strong> {rd.avg.toFixed(2)} kW</div>
          )}
          {rd.confidenceLevel && (
            <div><strong>{t('past_results.summary_confidence')}</strong>{' '}
              <Badge
                pill
                bg={rd.confidenceLevel === 'HIGH' ? 'success' : rd.confidenceLevel === 'MEDIUM_HIGH' ? 'info' : rd.confidenceLevel === 'MEDIUM' ? 'warning' : 'secondary'}
                style={{ fontSize: '0.75rem' }}
              >
                {rd.confidenceLevel.replace(/_/g, ' ')}
              </Badge>
            </div>
          )}
        </div>
        <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
          <Table striped bordered size="sm" responsive style={{ fontSize: 'var(--font-size-sm)' }}>
            <thead style={{ backgroundColor: 'var(--color-neutral-50)', position: 'sticky', top: 0 }}>
              <tr>
                <th>{t('past_results.th_hour')}</th>
                <th className="text-end">{t('past_results.th_forecast_kw')}</th>
                <th className="text-end">Lower Bound</th>
                <th className="text-end">Upper Bound</th>
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: rowCount }, (_, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 500 }}>{hours[i] != null ? `${String(hours[i]).padStart(2, '0')}:00` : i}</td>
                  <td className="text-end">{forecast[i] != null ? forecast[i].toFixed(2) : '—'}</td>
                  <td className="text-end">{lower[i] != null ? lower[i].toFixed(2) : '—'}</td>
                  <td className="text-end">{upper[i] != null ? upper[i].toFixed(2) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </>
    )
  }

  // --- Optimization: KPIs + hourly energy balance ---
  const renderOptimizationTable = () => {
    const baseline = rd.baselineKPIs || rd.baseline_kpis || {}
    const optimized = rd.optimizedKPIs || rd.optimized_kpis || {}
    const eb = rd.energyBalance || rd.energy_balance || {}
    const battery = rd.batterySchedule || rd.battery_schedule || {}

    const kpiRows = [
      [t('past_results.kpi_daily_cost'), 'daily_cost_eur', currency],
      [t('past_results.kpi_self_sufficiency'), 'self_sufficiency_pct', '%'],
      [t('past_results.kpi_self_consumption'), 'self_consumption_pct', '%'],
      [t('past_results.kpi_grid_import'), 'grid_import_kwh', 'kWh'],
      [t('past_results.kpi_grid_import_cost'), 'grid_import_cost_eur', currency],
      [t('past_results.kpi_grid_export_revenue'), 'grid_export_revenue_eur', currency],
      [t('past_results.kpi_peak_import'), 'peak_import_kw', 'kW'],
      [t('past_results.kpi_co2_avoided'), 'co2_avoided_kg', 'kg'],
    ]

    const hasKPIs = Object.keys(baseline).length > 0 || Object.keys(optimized).length > 0
    const ebHours = eb.hours || Array.from({ length: 24 }, (_, i) => i)
    const hasEB = (eb.load || []).length > 0

    return (
      <>
        {hasKPIs && (
          <>
            <h6 style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-2)' }}>{t('past_results.kpi_summary')}</h6>
            <Table striped bordered size="sm" responsive style={{ fontSize: 'var(--font-size-sm)' }} className="mb-4">
              <thead style={{ backgroundColor: 'var(--color-neutral-50)' }}>
                <tr>
                  <th>{t('past_results.th_metric')}</th>
                  <th className="text-end">{t('past_results.th_baseline')}</th>
                  <th className="text-end">{t('past_results.th_optimized')}</th>
                  <th>{t('past_results.th_unit')}</th>
                </tr>
              </thead>
              <tbody>
                {kpiRows.map(([label, key, unit]) => {
                  const bVal = baseline[key]
                  const oVal = optimized[key]
                  if (bVal == null && oVal == null) return null
                  return (
                    <tr key={key}>
                      <td style={{ fontWeight: 500 }}>{label}</td>
                      <td className="text-end">{bVal != null ? bVal.toFixed(2) : '—'}</td>
                      <td className="text-end">{oVal != null ? oVal.toFixed(2) : '—'}</td>
                      <td style={{ color: 'var(--color-neutral-500)' }}>{unit}</td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </>
        )}

        {hasEB && (
          <>
            <h6 style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-2)' }}>{t('past_results.hourly_energy_balance')}</h6>
            <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
              <Table striped bordered size="sm" responsive style={{ fontSize: 'var(--font-size-sm)' }}>
                <thead style={{ backgroundColor: 'var(--color-neutral-50)', position: 'sticky', top: 0 }}>
                  <tr>
                    <th>{t('past_results.th_hour')}</th>
                    <th className="text-end">{t('past_results.th_load_kw')}</th>
                    <th className="text-end">{t('past_results.th_pv_kw')}</th>
                    <th className="text-end">{t('past_results.th_grid_import')}</th>
                    <th className="text-end">{t('past_results.th_surplus')}</th>
                    <th className="text-end">{t('past_results.th_batt_charge')}</th>
                    <th className="text-end">{t('past_results.th_batt_discharge')}</th>
                  </tr>
                </thead>
                <tbody>
                  {ebHours.map((h, i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 500 }}>{String(h).padStart(2, '0')}:00</td>
                      <td className="text-end">{eb.load?.[i] != null ? eb.load[i].toFixed(1) : '—'}</td>
                      <td className="text-end">{eb.pv?.[i] != null ? eb.pv[i].toFixed(1) : '—'}</td>
                      <td className="text-end">{eb.gridImport?.[i] != null ? eb.gridImport[i].toFixed(1) : '—'}</td>
                      <td className="text-end">{eb.gridExport?.[i] != null ? eb.gridExport[i].toFixed(1) : '—'}</td>
                      <td className="text-end">{eb.batteryCharge?.[i] != null ? eb.batteryCharge[i].toFixed(1) : '—'}</td>
                      <td className="text-end">{eb.batteryDischarge?.[i] != null ? eb.batteryDischarge[i].toFixed(1) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </>
        )}

        {!hasKPIs && !hasEB && <p className="text-muted">{t('past_results.no_structured_data')}</p>}
      </>
    )
  }

  // --- CBA: scenario summaries ---
  const renderCBATable = () => {
    const scenarios = rd.scenarios || []
    if (scenarios.length === 0) return <p className="text-muted">{t('past_results.no_cba_scenarios')}</p>

    return (
      <Table striped bordered size="sm" responsive style={{ fontSize: 'var(--font-size-sm)' }}>
        <thead style={{ backgroundColor: 'var(--color-neutral-50)' }}>
          <tr>
            <th>{t('past_results.th_scenario')}</th>
            <th className="text-end">{t('past_results.th_npv', { currency })}</th>
            <th className="text-end">{t('past_results.th_bcr')}</th>
            <th className="text-end">{t('past_results.th_payback')}</th>
            <th className="text-end">{t('past_results.th_annual_savings', { currency })}</th>
            <th className="text-end">{t('past_results.th_co2_avoided')}</th>
          </tr>
        </thead>
        <tbody>
          {scenarios.map((s, i) => (
            <tr key={i}>
              <td style={{ fontWeight: 500 }}>{s.name || t('past_results.scenario_n', { n: i + 1 })}</td>
              <td className="text-end">{s.npv != null ? s.npv.toFixed(0) : '—'}</td>
              <td className="text-end">{s.bcr != null ? s.bcr.toFixed(2) : '—'}</td>
              <td className="text-end">{s.payback != null ? s.payback.toFixed(1) : 'N/A'}</td>
              <td className="text-end">{s.annualSavings != null ? s.annualSavings.toFixed(0) : '—'}</td>
              <td className="text-end">{s.co2Avoided != null ? s.co2Avoided.toFixed(2) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    )
  }

  // --- Generic fallback: render all key-value pairs ---
  const renderGenericTable = () => {
    const entries = Object.entries(rd)
    if (entries.length === 0) return <p className="text-muted">{t('past_results.modal_no_data')}</p>

    return (
      <div style={{ maxHeight: '450px', overflowY: 'auto' }}>
        <Table striped bordered size="sm" responsive style={{ fontSize: 'var(--font-size-sm)' }}>
          <thead style={{ backgroundColor: 'var(--color-neutral-50)', position: 'sticky', top: 0 }}>
            <tr>
              <th>{t('past_results.th_key')}</th>
              <th>{t('past_results.th_value')}</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([key, value]) => (
              <tr key={key}>
                <td style={{ fontWeight: 500 }}>{key}</td>
                <td style={{ maxWidth: '500px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {Array.isArray(value)
                    ? `[${value.length} items] ${value.slice(0, 5).join(', ')}${value.length > 5 ? '...' : ''}`
                    : typeof value === 'object' && value !== null
                      ? JSON.stringify(value, null, 2).substring(0, 200)
                      : String(value)}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    )
  }

  const renderContent = () => {
    if (resultType === 'forecast') return renderForecastTable()
    if (resultType === 'optimization') return renderOptimizationTable()
    if (resultType === 'cba') return renderCBATable()
    if (resultType === 'kpis') return renderOptimizationTable() // KPI results have similar shape
    return renderGenericTable()
  }

  const typeLabels = {
    forecast: t('past_results.type_forecast'),
    optimization: t('past_results.type_optimization'),
    cba: t('past_results.type_cba'),
    kpis: t('past_results.type_kpis'),
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered scrollable>
      <Modal.Header closeButton style={{ borderBottom: '2px solid var(--color-primary, #8CB808)' }}>
        <Modal.Title style={{ fontSize: 'var(--font-size-base)', fontWeight: 600 }}>
          {result.name || t('past_results.modal_default_title')}
          <Badge
            bg="none"
            className="ms-2"
            style={{
              backgroundColor: 'var(--color-primary-pale, #E8F4D9)',
              color: 'var(--color-primary-dark, #5A7A06)',
              fontSize: '0.7rem',
              verticalAlign: 'middle',
            }}
          >
            {typeLabels[resultType] || resultType}
          </Badge>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Metadata bar */}
        <div className="d-flex gap-4 mb-3 flex-wrap" style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
          {result.created_at && (
            <div>{t('past_results.meta_saved', { datetime: new Date(result.created_at).toLocaleString() })}</div>
          )}
          {params.target_date && <div>{t('past_results.meta_target_date', { date: params.target_date })}</div>}
          {params.asset && <div>{t('past_results.meta_asset', { asset: params.asset })}</div>}
          {params.forecast_mode && <div>{t('past_results.meta_mode', { mode: params.forecast_mode.replace('_', ' ') })}</div>}
          {params.forecast_type && <div>{t('past_results.meta_type', { type: params.forecast_type })}</div>}
          {params.objective && <div>{t('past_results.meta_objective', { objective: params.objective })}</div>}
        </div>

        {renderContent()}
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={() => exportResult(result, currency)}
        >
          {t('past_results.export_csv')}
        </Button>
        <Button variant="primary" size="sm" onClick={onHide}>
          {t('past_results.close')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------
function PastResultsPage() {
  const { community } = useOutletContext()
  const { id: communityId } = useParams()
  const { canEdit } = useAuth()
  const { t } = useTranslation()
  const [activeTab, setActiveTab] = useState('forecasts')
  const [forecastResults, setForecastResults] = useState([])
  const [optimizationResults, setOptimizationResults] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  // Modal state
  const [modalResult, setModalResult] = useState(null)
  const [modalLoading, setModalLoading] = useState(false)

  const currency = community?.currency || 'EUR'
  const FX_RATES = { CZK: 25, EUR: 1, PLN: 4.3, HUF: 395, RON: 5, BGN: 1.96 }
  const fxRate = FX_RATES[currency] || 1

  // Fetch results from backend API
  const fetchResults = useCallback(async () => {
    if (!communityId) return
    setIsLoading(true)
    setError(null)
    try {
      const allResults = await resultsAPI.list(communityId)
      const rawResults = Array.isArray(allResults) ? allResults : []

      // Filter out planning results (those with scenario_id in parameters).
      // Planning results are viewed in PlanningToolsPage via the "Saved" button.
      const results = rawResults.filter(r => !r.parameters?.scenario_id)

      const forecasts = results
        .filter(r => r.result_type === 'forecast')
        .map(r => ({
          id: r.id,
          date: r.created_at ? new Date(r.created_at).toLocaleDateString() : 'N/A',
          type: r.name || r.parameters?.forecast_type || t('past_results.default_forecast_type'),
          assets: r.parameters?.assets?.join(', ') || r.parameters?.asset_name || r.parameters?.asset || t('past_results.default_assets_community'),
          horizon: r.parameters?.horizon || '24h',
          confidenceLevel: r.result_data?.confidenceLevel || r.result_data?.confidence_level || r.parameters?.confidence_level || null,
        }))

      const optimizations = results
        .filter(r => ['optimization', 'cba', 'kpis'].includes(r.result_type))
        .map(r => ({
          id: r.id,
          date: r.created_at ? new Date(r.created_at).toLocaleDateString() : 'N/A',
          resultType: r.result_type,
          objective: r.parameters?.objective || r.name || r.result_type,
          cost: r.result_data?.optimizedKPIs?.daily_cost_eur
            ?? r.result_data?.optimized_kpis?.daily_cost_eur
            ?? null,
          selfSufficiency: r.result_data?.optimizedKPIs?.self_sufficiency_pct
            ?? r.result_data?.optimized_kpis?.self_sufficiency_pct
            ?? null,
          currency: currency,
          name: r.name,
        }))

      setForecastResults(forecasts)
      setOptimizationResults(optimizations)
    } catch (err) {
      console.error('Failed to fetch results:', err)
      setError(t('past_results.error_load'))
      setForecastResults([])
      setOptimizationResults([])
    } finally {
      setIsLoading(false)
    }
  }, [communityId, currency, t])

  useEffect(() => {
    fetchResults()
  }, [fetchResults])

  const handleDelete = async (resultId, type) => {
    if (!confirm(t('past_results.confirm_delete'))) return
    try {
      await resultsAPI.delete(communityId, resultId)
      if (type === 'forecast') {
        setForecastResults(prev => prev.filter(r => r.id !== resultId))
      } else {
        setOptimizationResults(prev => prev.filter(r => r.id !== resultId))
      }
    } catch (err) {
      console.error('Failed to delete result:', err)
      alert(t('past_results.alert_delete_failed'))
    }
  }

  const handleView = async (resultId) => {
    setModalLoading(true)
    try {
      const result = await resultsAPI.get(communityId, resultId)
      setModalResult(result)
    } catch (err) {
      console.error('Failed to fetch result detail:', err)
      alert(t('past_results.alert_view_failed'))
    } finally {
      setModalLoading(false)
    }
  }

  const handleExport = async (resultId) => {
    try {
      const result = await resultsAPI.get(communityId, resultId)
      exportResult(result, currency)
    } catch (err) {
      console.error('Failed to export result:', err)
      alert(t('past_results.alert_export_failed'))
    }
  }

  return (
    <div>
      {/* Page Header */}
      <div className="tool-page-header">
        <h1 className="tool-page-title">{t('past_results.header_title')}</h1>
        <p className="tool-page-subtitle">
          {t('past_results.header_subtitle', { community: community?.name || t('past_results.default_community') })}
        </p>
      </div>

      {error && (
        <Alert variant="warning" className="mb-3" style={{ fontSize: 'var(--font-size-sm)' }}>
          {error}
        </Alert>
      )}

      {/* Loading overlay for modal fetch */}
      {modalLoading && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1060,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: 'rgba(0,0,0,0.15)',
        }}>
          <Spinner animation="border" style={{ color: 'var(--color-primary)' }} />
        </div>
      )}

      {/* Result View Modal */}
      <ResultViewModal
        show={!!modalResult}
        onHide={() => setModalResult(null)}
        result={modalResult}
        currency={currency}
      />

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" style={{ color: 'var(--color-primary)' }} />
          <p className="mt-3" style={{ color: 'var(--color-neutral-600)' }}>{t('past_results.loading')}</p>
        </div>
      ) : (
        <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k)}>
          <Nav variant="pills" className="mb-4" style={{ gap: 'var(--space-2)' }}>
            <Nav.Item>
              <Nav.Link
                eventKey="forecasts"
                style={activeTab === 'forecasts' ? {
                  backgroundColor: 'var(--color-primary)',
                  color: 'white',
                  fontWeight: 600,
                } : {
                  backgroundColor: 'var(--color-neutral-100)',
                  color: 'var(--color-neutral-700)',
                }}
              >
                {t('past_results.tab_forecasts')}
                <Badge
                  pill
                  bg="none"
                  style={{
                    marginLeft: 'var(--space-2)',
                    backgroundColor: activeTab === 'forecasts' ? 'rgba(255,255,255,0.3)' : 'var(--color-neutral-300)',
                    color: activeTab === 'forecasts' ? 'white' : 'var(--color-neutral-700)',
                  }}
                >
                  {forecastResults.length}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link
                eventKey="optimizations"
                style={activeTab === 'optimizations' ? {
                  backgroundColor: 'var(--color-primary)',
                  color: 'white',
                  fontWeight: 600,
                } : {
                  backgroundColor: 'var(--color-neutral-100)',
                  color: 'var(--color-neutral-700)',
                }}
              >
                {t('past_results.tab_optimizations')}
                <Badge
                  pill
                  bg="none"
                  style={{
                    marginLeft: 'var(--space-2)',
                    backgroundColor: activeTab === 'optimizations' ? 'rgba(255,255,255,0.3)' : 'var(--color-neutral-300)',
                    color: activeTab === 'optimizations' ? 'white' : 'var(--color-neutral-700)',
                  }}
                >
                  {optimizationResults.length}
                </Badge>
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            {/* Forecasts Tab */}
            <Tab.Pane eventKey="forecasts">
              <Card style={{ border: '1px solid var(--color-neutral-200)' }}>
                <Card.Body className="p-0">
                  {forecastResults.length === 0 ? (
                    <div className="text-center py-5" style={{ color: 'var(--color-neutral-500)' }}>
                      <div style={{ fontSize: '2.5rem', opacity: 0.3 }}>📊</div>
                      <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 500, marginTop: 'var(--space-3)' }}>
                        {t('past_results.empty_forecasts_title')}
                      </p>
                      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-neutral-400)' }}>
                        {t('past_results.empty_forecasts_text')}
                      </p>
                    </div>
                  ) : (
                    <Table responsive hover className="mb-0" style={{ fontSize: 'var(--font-size-sm)' }}>
                      <thead style={{ backgroundColor: 'var(--color-neutral-50)' }}>
                        <tr>
                          <th>{t('past_results.th_date')}</th>
                          <th>{t('past_results.th_type')}</th>
                          <th>{t('past_results.th_assets')}</th>
                          <th>{t('past_results.th_horizon')}</th>
                          <th className="text-end">{t('past_results.th_confidence')}</th>
                          <th className="text-center">{t('past_results.th_actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {forecastResults.map((result) => (
                          <tr key={result.id}>
                            <td style={{ fontWeight: 500 }}>{result.date}</td>
                            <td>{result.type}</td>
                            <td style={{ color: 'var(--color-neutral-600)' }}>{result.assets}</td>
                            <td>
                              <Badge
                                pill
                                bg="none"
                                style={{ backgroundColor: 'var(--color-neutral-200)', color: 'var(--color-neutral-700)' }}
                              >
                                {result.horizon}
                              </Badge>
                            </td>
                            <td className="text-end">
                              {result.confidenceLevel ? (
                                <Badge
                                  pill
                                  bg={result.confidenceLevel === 'HIGH' ? 'success' : result.confidenceLevel === 'MEDIUM_HIGH' ? 'info' : result.confidenceLevel === 'MEDIUM' ? 'warning' : 'secondary'}
                                >
                                  {result.confidenceLevel.replace(/_/g, ' ')}
                                </Badge>
                              ) : (
                                <span style={{ color: 'var(--color-neutral-400)' }}>—</span>
                              )}
                            </td>
                            <td className="text-center">
                              <div className="d-flex gap-1 justify-content-center">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => handleView(result.id)}
                                  title={t('past_results.tooltip_view')}
                                >
                                  {t('past_results.btn_view')}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() => handleExport(result.id)}
                                  title={t('past_results.tooltip_export')}
                                >
                                  {t('past_results.btn_export')}
                                </Button>
                                {canEdit && (
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => handleDelete(result.id, 'forecast')}
                                    title={t('past_results.tooltip_delete')}
                                  >
                                    {t('past_results.btn_delete')}
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
                {forecastResults.length > 0 && (
                  <Card.Footer style={{ backgroundColor: 'var(--color-neutral-50)', fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
                    {t('past_results.showing_count', { count: forecastResults.length, total: forecastResults.length })}
                  </Card.Footer>
                )}
              </Card>
            </Tab.Pane>

            {/* Optimizations Tab */}
            <Tab.Pane eventKey="optimizations">
              <Card style={{ border: '1px solid var(--color-neutral-200)' }}>
                <Card.Body className="p-0">
                  {optimizationResults.length === 0 ? (
                    <div className="text-center py-5" style={{ color: 'var(--color-neutral-500)' }}>
                      <div style={{ fontSize: '2.5rem', opacity: 0.3 }}>⚡</div>
                      <p style={{ fontSize: 'var(--font-size-base)', fontWeight: 500, marginTop: 'var(--space-3)' }}>
                        {t('past_results.empty_optimizations_title')}
                      </p>
                      <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-neutral-400)' }}>
                        {t('past_results.empty_optimizations_text')}
                      </p>
                    </div>
                  ) : (
                    <Table responsive hover className="mb-0" style={{ fontSize: 'var(--font-size-sm)' }}>
                      <thead style={{ backgroundColor: 'var(--color-neutral-50)' }}>
                        <tr>
                          <th>{t('past_results.th_date')}</th>
                          <th>{t('past_results.th_type')}</th>
                          <th>{t('past_results.th_objective')}</th>
                          <th className="text-end">{t('past_results.th_cost', { currency })}</th>
                          <th className="text-end">{t('past_results.th_self_sufficiency')}</th>
                          <th className="text-center">{t('past_results.th_actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {optimizationResults.map((result) => (
                          <tr key={result.id}>
                            <td style={{ fontWeight: 500 }}>{result.date}</td>
                            <td>
                              <Badge
                                pill
                                bg="none"
                                style={{ backgroundColor: 'var(--color-neutral-100)', color: 'var(--color-neutral-700)' }}
                              >
                                {result.resultType}
                              </Badge>
                            </td>
                            <td>
                              <Badge
                                pill
                                bg="none"
                                style={{ backgroundColor: 'var(--color-primary-pale)', color: 'var(--color-primary-dark)' }}
                              >
                                {result.objective}
                              </Badge>
                            </td>
                            <td className="text-end" style={{ fontWeight: 600 }}>
                              {result.cost != null ? `${(result.cost * fxRate).toFixed(0)} ${currency}` : '—'}
                            </td>
                            <td className="text-end">
                              {result.selfSufficiency != null ? (
                                <Badge pill bg="success">{result.selfSufficiency.toFixed(1)}%</Badge>
                              ) : (
                                <span style={{ color: 'var(--color-neutral-400)' }}>—</span>
                              )}
                            </td>
                            <td className="text-center">
                              <div className="d-flex gap-1 justify-content-center">
                                <Button
                                  size="sm"
                                  variant="outline-primary"
                                  onClick={() => handleView(result.id)}
                                  title={t('past_results.tooltip_view')}
                                >
                                  {t('past_results.btn_view')}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  onClick={() => handleExport(result.id)}
                                  title={t('past_results.tooltip_export')}
                                >
                                  {t('past_results.btn_export')}
                                </Button>
                                {canEdit && (
                                  <Button
                                    size="sm"
                                    variant="outline-danger"
                                    onClick={() => handleDelete(result.id, 'optimization')}
                                    title={t('past_results.tooltip_delete')}
                                  >
                                    {t('past_results.btn_delete')}
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
                {optimizationResults.length > 0 && (
                  <Card.Footer style={{ backgroundColor: 'var(--color-neutral-50)', fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-600)' }}>
                    {t('past_results.showing_count', { count: optimizationResults.length, total: optimizationResults.length })}
                  </Card.Footer>
                )}
              </Card>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      )}
    </div>
  )
}

export default PastResultsPage
